import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Search, Phone, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import ServiceAreaMap from '../components/ServiceAreaMap';
import { COMPANY, SERVICE_AREAS, PRIMARY_AREAS, slugify } from '../data/mock';

const ServiceAreas = () => {
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => SERVICE_AREAS.filter((a) => a.toLowerCase().includes(query.trim().toLowerCase())),
    [query]
  );

  return (
    <div>
      <Seo
        path="/service-areas"
        title="Service Areas — Mold & Air Quality Testing Across Ontario"
        description="360 Mold Services provides mold inspection and indoor air quality testing across 46+ Southern Ontario communities — Kitchener, Waterloo, Toronto, Niagara, Muskoka, Peterborough and more."
      />
      <section className="brand-gradient relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#c7e79b]/40 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <Reveal>
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">Service Areas</span>
            <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold text-[#13251b] leading-tight">Serving all of {COMPANY.region}</h1>
            <p className="mt-5 text-lg text-[#44564b] max-w-2xl mx-auto">From the Region of Waterloo to the Greater Toronto Area and beyond — find your community below.</p>
            <div className="mt-8 max-w-md mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your city or town..."
                className="pl-12 h-12 rounded-full bg-white border-[#d3e6bd]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-14">
            <ServiceAreaMap />
          </Reveal>

          <Reveal>
            <h2 className="text-2xl font-bold text-[#13251b] mb-6">Primary regions</h2>
            <div className="flex flex-wrap gap-3">
              {PRIMARY_AREAS.map((a) => (
                <Link key={a} to={`/service-areas/${slugify(a)}`} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white text-sm font-semibold hover:bg-[#5c9a22] transition-colors">
                  <MapPin className="h-4 w-4" /> {a}
                </Link>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-14">
            <h2 className="text-2xl font-bold text-[#13251b] mb-6">All communities we serve</h2>
            {filtered.length === 0 ? (
              <div className="text-center py-12 bg-[#fafcf7] rounded-2xl">
                <p className="text-[#5b6a60]">We couldn’t find “{query}” in our list — but we likely still serve you.</p>
                <Button asChild className="mt-4 bg-brand hover:bg-[#5c9a22] text-white rounded-full px-6 font-semibold">
                  <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-1" /> Call to confirm</a>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {filtered.map((a) => (
                  <Link key={a} to={`/service-areas/${slugify(a)}`} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#fafcf7] border border-[#eef2e8] text-[#3d4c43] text-sm hover:border-brand hover:text-brand hover:bg-white transition-colors">
                    <MapPin className="h-4 w-4 text-brand shrink-0" /> {a}
                  </Link>
                ))}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-[#fafcf7]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <Reveal className="dark-section text-white rounded-[1.5rem] p-8 lg:p-12 text-center">
            <h2 className="text-3xl font-bold">Don’t see your town?</h2>
            <p className="mt-3 text-[#c3d2c6] max-w-xl mx-auto">We’re always expanding across Southern Ontario. Give us a call and we’ll let you know how we can help.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              <Button asChild className="bg-[#8bd44a] hover:bg-[#7bc23a] text-[#13251b] rounded-full px-7 h-12 font-semibold">
                <Link to="/contact">Request a Quote <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-7 h-12 font-semibold border-white/30 text-white hover:bg-white/10 bg-transparent">
                <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-1" /> {COMPANY.phone}</a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreas;
